import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, Building2, Hash } from 'lucide-react';
import PropTypes from 'prop-types';
import { API_URLS } from '../../../constants/api';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

const CourtDepartmentDropdown = ({
  isOpen,
  onClose,
  selectedCourt = '',
  selectedDepartments = [],
  onCourtChange,
  onDepartmentsChange,
  courts = [],
  jurisdiction,
  buttonRef
}) => {
  const [filteredCourts, setFilteredCourts] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [activeSection, setActiveSection] = useState('court'); // 'court' or 'departments'

  // Use smart positioning hook
  const { dropdownRef, position } = useDropdownPosition(isOpen, 450, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Set courts list
  useEffect(() => {
    setFilteredCourts(courts);
    setFocusedIndex(-1);
  }, [courts]);

  // Fetch departments when court changes
  useEffect(() => {
    // Use the correct jurisdiction for this system (hardcoded like the original)
    const correctJurisdiction =
      'California - Los Angeles County Superior Courts';

    if (selectedCourt) {
      setDepartmentsLoading(true);

      // Strip jurisdiction prefix from courthouse name if present
      const courthouseName = selectedCourt.replace(/^[^-]+ - /, '');
      const url = `${API_URLS.DEPARTMENTS}?jurisdiction=${encodeURIComponent(correctJurisdiction)}&courthouse=${encodeURIComponent(courthouseName)}`;

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          setAvailableDepartments(data);
          setActiveSection('departments');
        })
        .catch((error) => {
          console.error('Error fetching departments:', error);
          setAvailableDepartments([]);
        })
        .finally(() => {
          setDepartmentsLoading(false);
        });
    } else {
      setAvailableDepartments([]);
      setActiveSection('court');
    }
  }, [selectedCourt]);

  const handleDepartmentToggle = useCallback(
    (dept) => {
      // Ensure consistent string comparison
      const deptStr = String(dept);
      const selectedStr = selectedDepartments.map((d) => String(d));
      const isSelected = selectedStr.includes(deptStr);

      if (isSelected) {
        const newDepartments = selectedDepartments.filter(
          (d) => String(d) !== deptStr
        );
        onDepartmentsChange(newDepartments);
      } else {
        const newDepartments = [...selectedDepartments, deptStr];
        onDepartmentsChange(newDepartments);
      }
    },
    [selectedDepartments, onDepartmentsChange]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a filter button - if so, don't close
      const isFilterButton = event.target.closest('[data-filter-button]');
      if (isFilterButton) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      const maxIndex =
        activeSection === 'court'
          ? filteredCourts.length - 1
          : availableDepartments.length - 1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0) {
            if (activeSection === 'court') {
              const court = filteredCourts[focusedIndex];
              onCourtChange(court);
              if (court) {
                setActiveSection('departments');
              }
            } else {
              const dept = availableDepartments[focusedIndex];
              handleDepartmentToggle(dept);
            }
          }
          break;
        case 'Escape':
          onClose();
          break;
        case 'Tab':
          if (activeSection === 'court' && selectedCourt) {
            e.preventDefault();
            setActiveSection('departments');
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    focusedIndex,
    filteredCourts,
    availableDepartments,
    activeSection,
    selectedCourt,
    onClose,
    onCourtChange,
    handleDepartmentToggle
  ]);

  const handleCourtSelect = (court) => {
    onCourtChange(court);
    if (court) {
      setActiveSection('departments');
    }
  };

  const handleBackToCourt = () => {
    setActiveSection('court');
  };

  const clearCourt = () => {
    onCourtChange('');
    onDepartmentsChange([]);
    setActiveSection('court');
  };

  const clearDepartments = () => {
    onDepartmentsChange([]);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-[450px]'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[600px] overflow-hidden`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '320px'
              }
        }
      >
        {/* Header */}
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-900">
                {activeSection === 'court'
                  ? 'Select Courthouse'
                  : 'Select Departments'}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Court Selection */}
        {activeSection === 'court' && (
          <div className="p-3">
            {/* Search Input */}

            {/* Selected Court Display */}
            {selectedCourt && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedCourt.replace(/^[^-]+ - /, '')}
                  </span>
                  <button
                    onClick={clearCourt}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Court List */}
            <div className="max-h-48 overflow-y-auto p-2">
              {filteredCourts.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No courthouses found
                </div>
              ) : (
                filteredCourts.map((court, index) => (
                  <button
                    key={court}
                    onClick={() => handleCourtSelect(court)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors box-border ${
                      index === focusedIndex
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'hover:bg-gray-100 text-gray-700 border border-transparent'
                    }`}
                  >
                    {court.replace(/^[^-]+ - /, '')}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Department Selection */}
        {activeSection === 'departments' && (
          <div className="p-3 pb-4">
            {/* Back Button and Selected Court */}
            <div className="mb-3">
              <button
                onClick={handleBackToCourt}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mb-2"
              >
                ← Back to courthouse
              </button>
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-sm font-medium text-blue-700">
                  {selectedCourt.replace(/^[^-]+ - /, '')}
                </span>
              </div>
            </div>

            {/* Department Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  Departments
                </span>
              </div>
              {selectedDepartments.length > 0 && (
                <button
                  onClick={clearDepartments}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Department List */}
            <div className="max-h-96 overflow-y-auto w-full p-2">
              {departmentsLoading ? (
                <div className="text-center py-4 text-gray-500">
                  Loading departments...
                </div>
              ) : availableDepartments.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No departments available for {selectedCourt}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 pb-2 w-full">
                  {availableDepartments.map((dept, index) => {
                    const isSelected = selectedDepartments
                      .map((d) => String(d))
                      .includes(String(dept));
                    return (
                      <button
                        key={dept}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDepartmentToggle(dept);
                        }}
                        onMouseEnter={() => setFocusedIndex(index)}
                        className={`p-1.5 sm:p-2 text-left text-xs border rounded-md transition-colors ${
                          isSelected
                            ? 'bg-blue-50 border-blue-200 text-blue-900'
                            : index === focusedIndex
                              ? 'bg-gray-50 border-gray-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">D {dept}</span>
                          {isSelected && (
                            <Check className="w-3 h-3 text-blue-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selected Departments Summary */}
            {selectedDepartments.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap gap-1">
                  {selectedDepartments.map((dept) => (
                    <span
                      key={dept}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700"
                    >
                      {dept}
                      <button
                        onClick={() => handleDepartmentToggle(dept)}
                        className="ml-1 text-blue-400 hover:text-blue-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

CourtDepartmentDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCourt: PropTypes.string,
  selectedDepartments: PropTypes.arrayOf(PropTypes.string),
  onCourtChange: PropTypes.func.isRequired,
  onDepartmentsChange: PropTypes.func.isRequired,
  courts: PropTypes.arrayOf(PropTypes.string),
  jurisdiction: PropTypes.string,
  buttonRef: PropTypes.object
};

export default CourtDepartmentDropdown;
